// frontend/src/screens/WelcomeScreen.jsx
import React, { useContext, useEffect, useRef, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import { useTranslation } from "react-i18next";
import { platform, shouldPresentPwaInstall } from "../platform";

const WelcomeScreen = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { user } = useContext(AuthContext);
  const canvasRef = useRef(null);

  // ===================== PWA Install (Welcome) =====================
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [showInstallHelp, setShowInstallHelp] = useState(false);
  const [installHelpMode, setInstallHelpMode] = useState("generic"); // 'ios' | 'ios_inapp' | 'android' | 'android_inapp' | 'generic'


  const isIOS = useMemo(() => {
    const ua = navigator.userAgent || "";

    // iPhone / iPad / iPod
    if (/iphone|ipad|ipod/i.test(ua)) return true;

    // iPadOS moderno (Safari modo desktop)
    // Heuristic: MacIntel + touch points
    try {
      return navigator.platform === "MacIntel" && Number(navigator.maxTouchPoints || 0) > 1;
    } catch {
      return false;
    }
  }, []);

  const isMac = useMemo(() => {
    try {
      return navigator.platform === "MacIntel" && Number(navigator.maxTouchPoints || 0) === 0;
    } catch {
      return false;
    }
  }, []);

  const isAndroid = useMemo(() => {
    const ua = navigator.userAgent || "";
    return /android/i.test(ua);
  }, []);

  const inAppBrowserLabel = useMemo(() => {
    const ua = (navigator.userAgent || '').toLowerCase();

    if (ua.includes('threads') || ua.includes('barcelona')) return 'Threads';
    if (ua.includes('instagram')) return 'Instagram';
    if (ua.includes('tiktok') || ua.includes('musical_ly') || ua.includes('ttwebview') || ua.includes('tiktokwebview') || ua.includes('aweme')) return 'TikTok';
    if (ua.includes('messenger')) return 'Messenger';
    if (ua.includes('fban') || ua.includes('fbav') || ua.includes('fb_iab')) return 'Facebook';
    if (ua.includes('twitter') || ua.includes('x/')) return 'X';
    if (ua.includes('line')) return 'LINE';
    return 'una red social';
  }, []);

  const isInAppBrowser = useMemo(() => {
    const ua = (navigator.userAgent || "").toLowerCase();

    // Common in-app browsers / webviews:
    // Instagram/FB/Messenger/Threads + TikTok variants + Twitter/X + Line
    const isMeta =
      ua.includes("instagram") ||
      ua.includes("fban") ||
      ua.includes("fbav") ||
      ua.includes("fb_iab") ||
      ua.includes("messenger") ||
      ua.includes("threads") ||
      ua.includes("barcelona");

    // TikTok UA can vary across iOS/Android:
    // - "tiktok" (common)
    // - "musical_ly" (older)
    // - "ttwebview" / "tiktokwebview" (some builds)
    // - "aweme" (TikTok internal app name in some regions)
    const isTikTok =
      ua.includes("tiktok") ||
      ua.includes("musical_ly") ||
      ua.includes("ttwebview") ||
      ua.includes("tiktokwebview") ||
      ua.includes("aweme");

    const isOther = ua.includes("twitter") || ua.includes("x/") || ua.includes("line");

    return isMeta || isTikTok || isOther;
  }, []);

  const shouldOpenInBrowser = useMemo(() => {
    // In-app browsers often block the native install UX.
    // Android webviews: beforeinstallprompt often won't appear.
    // iOS IG/FB webviews: Safari Share-sheet install path isn't available here.
    return (isAndroid && isInAppBrowser && !deferredPrompt) || (isIOS && isInAppBrowser);
  }, [isAndroid, isIOS, isInAppBrowser, deferredPrompt]);


  const isStandalone = useMemo(() => {
    // iOS: navigator.standalone; others: display-mode
    // eslint-disable-next-line no-restricted-globals
    return (window.navigator && window.navigator.standalone) ||
      (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches);
  }, []);

  useEffect(() => {
    setIsInstalled(!!isStandalone);
  }, [isStandalone]);

  useEffect(() => {
    if (platform.isNative) return undefined;

    const onBeforeInstallPrompt = (e) => {
      // Chrome/Edge/Android: capture the prompt so we can trigger it from our custom button
      e.preventDefault();
      setDeferredPrompt(e);
    };

    const onAppInstalled = () => {
      setIsInstalled(true);
      setDeferredPrompt(null);
      setShowInstallHelp(false);
    };

    window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt);
    window.addEventListener("appinstalled", onAppInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstallPrompt);
      window.removeEventListener("appinstalled", onAppInstalled);
    };
  }, []);

  const handleInstallClick = async () => {
    try {
      // Premium path: in-app browsers (Instagram/Facebook/etc.) → open in external browser first
      if (shouldOpenInBrowser) {
        if (isIOS && isInAppBrowser) setInstallHelpMode("ios_inapp");
        else setInstallHelpMode("android_inapp");
        setShowInstallHelp(true);
        return;
      }

      // Native prompt (Chrome/Edge/Android and some desktop browsers)
      if (deferredPrompt) {
        const promptEvt = deferredPrompt;
        setDeferredPrompt(null);
        await promptEvt.prompt();
        const choice = await promptEvt.userChoice;
        if (choice && choice.outcome === "accepted") {
          setShowInstallHelp(false);
        }
        return;
      }

      // Guidance modes
      if (isIOS) setInstallHelpMode("ios");
      else if (isAndroid) setInstallHelpMode("android");
      else setInstallHelpMode("generic");

      setShowInstallHelp(true);
    } catch (e) {}
  };

  const installCtaText = useMemo(() => {
    // Android/iOS in-app browsers: best UX is to open in the external browser
    if (shouldOpenInBrowser) {
      // En navegadores embebidos (Instagram/Facebook/etc.) no podemos abrir el navegador externo
      // de forma confiable. Mostramos un CTA más “action-oriented” y luego la guía.
      return t("welcome.installApp");
    }

    if (isIOS) return t("welcome.installIOS");
    if (isAndroid && deferredPrompt) return t("welcome.installNow");
    if (isMac) return t("welcome.installMac");
    if (deferredPrompt) return t("welcome.installNow");
    return t("welcome.howToInstall");
  }, [shouldOpenInBrowser, isIOS, isAndroid, isMac, deferredPrompt, t]);

  const installDialogCopy = useMemo(() => {
    const isEs = i18n.language?.startsWith("es");
    const browser = inAppBrowserLabel || (isEs ? "esta app" : "this app");

    const copy = {
      es: {
        iosTitle: "Instalar Zodiacus en iPhone",
        iosInAppTitle: `Abrí Zodiacus en Safari`,
        androidTitle: "Instalar Zodiacus",
        genericTitle: "Instalar Zodiacus",
        iosStep1: "Tocá el botón Compartir de Safari.",
        iosStep2: "Elegí “Agregar a pantalla de inicio”.",
        iosInAppIntro: `Estás navegando dentro de ${browser}. Para instalar Zodiacus, primero abrilo en Safari.`,
        androidInAppIntro: `Estás navegando dentro de ${browser}. Para instalar Zodiacus, primero abrilo en Chrome o en tu navegador.`,
        tapDotsTopRight: "Tocá los tres puntos o el menú de arriba.",
        openExternalBrowser: "Elegí “Abrir en navegador” o “Abrir en Safari”.",
        openBrowser: "Elegí “Abrir en Chrome” o “Abrir en navegador”.",
        thenInBrowser: "Después volvé a tocar “Instalar app” desde el navegador.",
        openMenu: "Abrí el menú del navegador.",
        installOrAddHome: "Elegí “Instalar app” o “Agregar a pantalla de inicio”.",
        understood: "Entendido",
        close: "Cerrar",
      },
      en: {
        iosTitle: "Install Zodiacus on iPhone",
        iosInAppTitle: `Open Zodiacus in Safari`,
        androidTitle: "Install Zodiacus",
        genericTitle: "Install Zodiacus",
        iosStep1: "Tap Safari’s Share button.",
        iosStep2: "Choose “Add to Home Screen”.",
        iosInAppIntro: `You’re browsing inside ${browser}. To install Zodiacus, open it in Safari first.`,
        androidInAppIntro: `You’re browsing inside ${browser}. To install Zodiacus, open it in Chrome or your browser first.`,
        tapDotsTopRight: "Tap the three dots or the menu at the top.",
        openExternalBrowser: "Choose “Open in browser” or “Open in Safari”.",
        openBrowser: "Choose “Open in Chrome” or “Open in browser”.",
        thenInBrowser: "Then tap “Install app” again from the browser.",
        openMenu: "Open your browser menu.",
        installOrAddHome: "Choose “Install app” or “Add to Home Screen”.",
        understood: "Got it",
        close: "Close",
      },
    };

    return isEs ? copy.es : copy.en;
  }, [i18n.language, inAppBrowserLabel]);

  useEffect(() => {
    if (user) navigate("/home");
  }, [user, navigate]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Stars canvas is currently disabled in CSS (`display: none`), so avoid spinning a RAF loop.
    let canvasVisible = true;
    try {
      const cs = window.getComputedStyle(canvas);
      canvasVisible = cs.display !== 'none' && cs.visibility !== 'hidden' && cs.opacity !== '0';
    } catch {
      canvasVisible = true;
    }
    if (!canvasVisible) return;

    const ctx = canvas.getContext("2d", { alpha: true });

    function resize() {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.floor(window.innerWidth * dpr);
      canvas.height = Math.floor(window.innerHeight * dpr);
      canvas.style.width = `${window.innerWidth}px`;
      canvas.style.height = `${window.innerHeight}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }

    resize();

    const STAR_DENSITY = 14000; // px² por estrella (más chico = más estrellas) — menos puntos, más limpio
    function buildStars() {
      const count = Math.min(320, Math.max(100, Math.floor((window.innerWidth * window.innerHeight) / STAR_DENSITY)));
      const arr = new Array(count).fill(0).map(() => ({
        x: Math.random() * window.innerWidth,
        y: Math.random() * window.innerHeight,
        r: Math.random() * 1.0 + 0.35, // estrellas finas, pero presentes
        alpha: Math.random() * 0.4 + 0.3, // brillo base moderado
        drift: (Math.random() * 0.9 + 0.2) * 0.018, // twinkle suave
        // ~50% doradas (hues ~46-60), resto blancas
        hue: Math.random() < 0.5 ? 46 + Math.random() * 14 : 0,
        golden: Math.random() < 0.5,
        bright: Math.random() < 0.06, // pocas muy brillantes
        glowBase: Math.random() * 4 + 3
      }));
      return arr;
    }

    let stars = buildStars();

    let raf;
    function tick() {
      ctx.clearRect(0, 0, window.innerWidth, window.innerHeight);
      for (const s of stars) {
        // Twinkle sutil
        s.alpha += (Math.random() - 0.5) * s.drift;
        if (s.alpha < 0.15) s.alpha = 0.15;
        if (s.alpha > 1.0) s.alpha = 1.0;

        // Glow: blur suave; más intenso si es bright o dorada
        const blur = s.glowBase + (s.bright ? 6 : 0) + (s.golden ? 2 : 0);
        ctx.shadowBlur = blur;
        ctx.shadowColor = s.golden
          ? `hsla(${s.hue}, 96%, 72%, ${Math.min(0.95, s.alpha)})`
          : `rgba(255,255,255,${Math.min(0.9, s.alpha)})`;

        ctx.beginPath();
        ctx.arc(s.x, s.y, s.r * (s.bright ? 1.25 : 1), 0, Math.PI * 2);
        ctx.closePath();
        ctx.fillStyle = s.golden
          ? `hsla(${s.hue}, 95%, 78%, ${s.alpha})`
          : `rgba(255,255,255,${s.alpha})`;
        ctx.fill();

        // reset shadow to avoid leaking to other drawings (optional but safer)
        ctx.shadowBlur = 0;
      }
      raf = requestAnimationFrame(tick);
    }

    raf = requestAnimationFrame(tick);

    // Rebuild stars on significant resize/orientation changes
    let rebuildTimer;
    const rebuild = () => {
      clearTimeout(rebuildTimer);
      rebuildTimer = setTimeout(() => {
        resize();
        stars = buildStars();
      }, 120);
    };
    window.addEventListener("orientationchange", rebuild);
    window.addEventListener("resize", rebuild);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", rebuild);
      window.removeEventListener("orientationchange", rebuild);
    };
  }, []);

  return (
    <div
      id="welcomeRoot"
      style={{
        minHeight: "100vh",
        background:
          "radial-gradient(circle at 50% 28%, rgba(116,25,188,0.36) 0%, rgba(36,0,78,0.38) 28%, rgba(5,2,18,0.98) 72%), linear-gradient(180deg, #05010F 0%, #130026 48%, #05010F 100%)",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        color: "#fff",
        textAlign: "center",
        position: "relative",
        zIndex: 1,
        overflow: "hidden",
        isolation: "isolate"
      }}
    >
      <canvas aria-hidden="true" ref={canvasRef} className="starsCanvas" />
      <div aria-hidden="true" className="wheel" style={styles.wheel} />

      <div id="welcomeLangTop" style={styles.langTop} aria-label={t("welcome.languageSelector")}>
        <span style={styles.langIcon}>◎</span>
        <button
          type="button"
          onClick={() => i18n.changeLanguage("es")}
          style={{
            ...styles.langTopButton,
            opacity: i18n.language?.startsWith("es") ? 1 : 0.55,
          }}
        >
          ES
        </button>
        <span style={styles.langDivider}>/</span>
        <button
          type="button"
          onClick={() => i18n.changeLanguage("en")}
          style={{
            ...styles.langTopButton,
            opacity: i18n.language?.startsWith("en") ? 1 : 0.55,
          }}
        >
          EN
        </button>
      </div>

      <main id="welcomeCenter" style={styles.centerFixed}>
        <section style={styles.heroCard}>
          <div style={styles.orbitWrap}>
            <div aria-hidden="true" style={styles.orbitGlow} />
            <div aria-hidden="true" style={styles.orbitRing} />
            <div aria-hidden="true" className="welcomeHeroWheel" style={styles.heroWheel} />
            <div aria-hidden="true" style={styles.centerZ}>Z</div>
          </div>

          <h1 id="welcomeTitle" style={styles.title}>Zodiacus</h1>
          <p style={styles.subtitle}>{t("welcome.subtitle", "Tu universo zodiacal en una sola app")}</p>

          <div id="welcomeActions" style={styles.actions}>
            <button onClick={() => navigate("/login")} style={{ ...styles.btn, ...styles.primaryBtn }}>
              <span aria-hidden="true" style={styles.btnSpark}>✦</span>
              {t("common.login")}
              <span aria-hidden="true" style={styles.btnSpark}>✦</span>
            </button>

            <button onClick={() => navigate("/register")} style={{ ...styles.btn, ...styles.secondaryBtn }}>
              {t("common.register")}
            </button>

            {shouldPresentPwaInstall({ isInstalled }) && (
              <button
                onClick={handleInstallClick}
                aria-label={t("welcome.installAria")}
                style={{ ...styles.btn, ...styles.installBtn }}
              >
                {installCtaText}
              </button>
            )}
          </div>
        </section>

        <div aria-hidden="true" style={styles.bottomSigil}>
          <span />
          <i>✦</i>
          <span />
        </div>
      </main>

      {platform.isWeb && showInstallHelp && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={t("welcome.installDialogAria")}
          onClick={() => setShowInstallHelp(false)}
          style={styles.installOverlay}
        >
          <div onClick={(e) => e.stopPropagation()} style={styles.installDialog}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, marginBottom: 10 }}>
              <strong style={{ fontSize: "1.05rem", color: "#FFE8A8" }}>
                {installHelpMode === "ios"
                  ? installDialogCopy.iosTitle
                  : installHelpMode === "ios_inapp"
                  ? installDialogCopy.iosInAppTitle
                  : installHelpMode === "android_inapp"
                  ? installDialogCopy.androidTitle
                  : installDialogCopy.genericTitle}
              </strong>
              <button onClick={() => setShowInstallHelp(false)} aria-label={installDialogCopy.close} style={styles.closeInstallBtn}>
                ✕
              </button>
            </div>

            {installHelpMode === "ios" ? (
              <div>
                <p><b>1.</b> {installDialogCopy.iosStep1}</p>
                <p><b>2.</b> {installDialogCopy.iosStep2}</p>
              </div>
            ) : installHelpMode === "ios_inapp" ? (
              <div>
                <p style={{ margin: "0 0 10px" }}>{installDialogCopy.iosInAppIntro}</p>
                <p><b>1.</b> {installDialogCopy.tapDotsTopRight}</p>
                <p><b>2.</b> {installDialogCopy.openExternalBrowser}</p>
                <p style={{ marginTop: 10 }}>{installDialogCopy.thenInBrowser}</p>
              </div>
            ) : installHelpMode === "android_inapp" ? (
              <div>
                <p style={{ margin: "0 0 10px" }}>{installDialogCopy.androidInAppIntro}</p>
                <p><b>1.</b> {installDialogCopy.tapDotsTopRight}</p>
                <p><b>2.</b> {installDialogCopy.openBrowser}</p>
                <p style={{ marginTop: 10 }}>{installDialogCopy.thenInBrowser}</p>
              </div>
            ) : (
              <div>
                <p><b>1.</b> {installDialogCopy.openMenu}</p>
                <p><b>2.</b> {installDialogCopy.installOrAddHome}</p>
              </div>
            )}

            <div style={{ marginTop: 12, textAlign: "center" }}>
              <button onClick={() => setShowInstallHelp(false)} style={{ ...styles.btn, ...styles.secondaryBtn }}>
                {installDialogCopy.understood}
              </button>
            </div>
          </div>
        </div>
      )}


      <div className="goldEdgeTop" />
      <div className="goldEdgeBottom" />

      {/* Solo CSS */}
      <style>{`
        #welcomeRoot .starsCanvas{
          position: fixed;
          inset: 0;
          opacity: 0;
          display: none;
          pointer-events: none;
        }

        #welcomeRoot .wheel {
          display: none;
        }

        #welcomeRoot::before {
          content: "";
          position: fixed;
          inset: -20%;
          z-index: -4;
          pointer-events: none;
          background:
            radial-gradient(circle at 18% 18%, rgba(177,74,255,0.26) 0%, transparent 25%),
            radial-gradient(circle at 84% 62%, rgba(123,31,205,0.22) 0%, transparent 31%),
            radial-gradient(circle at 50% 108%, rgba(218,165,32,0.16) 0%, transparent 30%);
          filter: blur(2px);
        }

        #welcomeRoot::after {
          content: "";
          position: fixed;
          inset: 0;
          z-index: -3;
          pointer-events: none;
          background-image:
            radial-gradient(circle, rgba(255,226,138,0.98) 0 1px, transparent 1.5px),
            radial-gradient(circle, rgba(255,255,255,0.72) 0 1px, transparent 1.4px),
            linear-gradient(180deg, rgba(0,0,0,0.12), rgba(0,0,0,0.72));
          background-size: 110px 110px, 170px 170px, 100% 100%;
          background-position: 20px 18px, 80px 42px, center;
          opacity: 0.62;
          animation: star-drift 18s linear infinite;
        }

        @keyframes star-drift {
          from { background-position: 20px 18px, 80px 42px, center; }
          to { background-position: 20px 128px, 80px 212px, center; }
        }

        @keyframes wheel-spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        @keyframes float-soft {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-6px); }
        }

        @media (prefers-reduced-motion: reduce) {
          #welcomeRoot::after,
          #welcomeRoot .welcomeHeroWheel {
            animation: none !important;
          }
        }

        #welcomeRoot .goldEdgeTop,
        #welcomeRoot .goldEdgeBottom {
          position: fixed;
          left: 0;
          right: 0;
          height: 1px;
          z-index: 0;
          pointer-events: none;
          background: linear-gradient(90deg, transparent, rgba(218,165,32,0.42), transparent);
        }

        #welcomeRoot .goldEdgeTop { top: 0; }
        #welcomeRoot .goldEdgeBottom { bottom: 0; }

        #welcomeRoot .welcomeHeroWheel {
          animation: wheel-spin 150s linear infinite;
        }

        #welcomeCenter {
          animation: float-soft 7s ease-in-out infinite;
        }

        @media (max-width: 820px) {
          #welcomeCenter {
            width: 100% !important;
            padding: 0 18px !important;
          }

          #welcomeTitle {
            font-size: clamp(2.35rem, 11vw, 3.8rem) !important;
            letter-spacing: 0.42rem !important;
          }

          #welcomeActions {
            width: min(100%, 520px) !important;
          }

          #welcomeActions button {
            width: min(100%, 510px) !important;
          }
        }

        @media (min-width: 821px) {
          #welcomeCenter {
            width: min(520px, 92vw) !important;
          }
        }
      `}</style>
    </div>
  );
};

export default WelcomeScreen;

const styles = {
  centerFixed: {
    position: "fixed",
    inset: 0,
    margin: "auto",
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 18,
    textAlign: "center",
    zIndex: 100,
    padding: "78px 24px 34px",
    boxSizing: "border-box"
  },
  langTop: {
    position: "fixed",
    top: "calc(18px + env(safe-area-inset-top, 0px))",
    right: "calc(18px + env(safe-area-inset-right, 0px))",
    zIndex: 250,
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "0.62rem 0.9rem",
    borderRadius: "999px",
    border: "1px solid rgba(232,95,255,0.72)",
    background: "rgba(14,4,31,0.72)",
    color: "#FFF0B8",
    boxShadow: "0 0 22px rgba(190,60,255,0.34), inset 0 0 18px rgba(218,165,32,0.08)",
    backdropFilter: "blur(12px)",
    WebkitBackdropFilter: "blur(12px)"
  },
  langIcon: {
    fontSize: "1.1rem",
    color: "#FFE8A8",
    lineHeight: 1
  },
  langDivider: {
    color: "rgba(255,232,168,0.45)",
    fontWeight: 800
  },
  langTopButton: {
    border: 0,
    background: "transparent",
    color: "#FFF0B8",
    fontWeight: 900,
    letterSpacing: "0.06em",
    fontSize: "0.95rem",
    cursor: "pointer",
    padding: 0
  },
  heroCard: {
    width: "min(520px, 100%)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 16
  },
  orbitWrap: {
    position: "relative",
    width: "min(72vw, 335px)",
    height: "min(72vw, 335px)",
    display: "grid",
    placeItems: "center",
    marginBottom: 6
  },
  orbitGlow: {
    position: "absolute",
    inset: "8%",
    borderRadius: "50%",
    background: "radial-gradient(circle, rgba(218,165,32,0.18) 0%, rgba(154,47,255,0.22) 45%, transparent 72%)",
    filter: "blur(8px)",
    boxShadow: "0 0 80px rgba(171,54,255,0.54)"
  },
  orbitRing: {
    position: "absolute",
    inset: "4%",
    borderRadius: "50%",
    border: "1px solid rgba(255,218,116,0.42)",
    boxShadow: "0 0 24px rgba(218,165,32,0.28), inset 0 0 24px rgba(157,58,255,0.18)"
  },
  heroWheel: {
    position: "relative",
    width: "100%",
    height: "100%",
    borderRadius: "50%",
    backgroundImage: 'url("/logo/Zodiacus.jpeg")',
    backgroundRepeat: "no-repeat",
    backgroundPosition: "center center",
    backgroundSize: "contain",
    filter: "drop-shadow(0 0 22px rgba(218,165,32,0.26)) drop-shadow(0 0 36px rgba(154,47,255,0.32))"
  },
  centerZ: {
    position: "absolute",
    width: "31%",
    height: "31%",
    borderRadius: "50%",
    display: "grid",
    placeItems: "center",
    color: "#FFD979",
    fontFamily: "'Cinzel', 'Cormorant Garamond', serif",
    fontWeight: 900,
    fontSize: "clamp(3.4rem, 13vw, 5.6rem)",
    lineHeight: 1,
    textShadow: "0 4px 14px rgba(0,0,0,0.72), 0 0 22px rgba(218,165,32,0.38)",
    pointerEvents: "none"
  },
  title: {
    margin: "0.05rem 0 0",
    color: "#FFE09A",
    fontSize: "clamp(2.6rem, 8vw, 4.7rem)",
    fontWeight: 500,
    textTransform: "uppercase",
    letterSpacing: "0.55rem",
    textIndent: "0.55rem",
    fontFamily: "'Cinzel', 'Cormorant Garamond', serif",
    textShadow: "0 2px 10px rgba(0,0,0,0.9), 0 0 26px rgba(218,165,32,0.32)",
    lineHeight: 1
  },
  subtitle: {
    margin: "-2px 0 10px",
    color: "rgba(255,232,168,0.78)",
    fontSize: "clamp(0.84rem, 2.6vw, 1rem)",
    letterSpacing: "0.16em",
    textTransform: "uppercase",
    maxWidth: 420
  },
  actions: {
    display: "flex",
    flexDirection: "column",
    gap: "1rem",
    width: "min(100%, 510px)",
    alignItems: "center",
    marginTop: "0.15rem"
  },
  btn: {
    width: "100%",
    minHeight: 58,
    borderRadius: "999px",
    fontSize: "clamp(0.95rem, 3.4vw, 1.15rem)",
    cursor: "pointer",
    fontWeight: 900,
    letterSpacing: "0.24em",
    textTransform: "uppercase",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 16,
    transition: "transform 140ms ease, box-shadow 140ms ease, filter 140ms ease",
    WebkitTapHighlightColor: "transparent"
  },
  primaryBtn: {
    border: "1px solid rgba(255,230,147,0.95)",
    background: "linear-gradient(180deg, #FFE08B 0%, #D9902E 48%, #9B551B 100%)",
    color: "#1E1230",
    boxShadow: "0 0 28px rgba(218,165,32,0.48), 0 0 22px rgba(205,69,255,0.42), inset 0 1px 0 rgba(255,255,255,0.55)"
  },
  secondaryBtn: {
    border: "1px solid rgba(232,95,255,0.82)",
    background: "rgba(12,2,30,0.42)",
    color: "#FFE8A8",
    boxShadow: "0 0 24px rgba(196,69,255,0.36), inset 0 0 18px rgba(218,165,32,0.05)",
    backdropFilter: "blur(10px)",
    WebkitBackdropFilter: "blur(10px)"
  },
  installBtn: {
    minHeight: 48,
    fontSize: "clamp(0.78rem, 2.8vw, 0.92rem)",
    letterSpacing: "0.12em",
    border: "1px solid rgba(218,165,32,0.62)",
    background: "rgba(0,0,0,0.28)",
    color: "rgba(255,232,168,0.92)",
    boxShadow: "0 0 18px rgba(218,165,32,0.18)",
    backdropFilter: "blur(8px)",
    WebkitBackdropFilter: "blur(8px)"
  },
  btnSpark: {
    fontSize: "1.05rem",
    color: "rgba(255,255,255,0.9)",
    textShadow: "0 0 10px rgba(255,255,255,0.8)"
  },
  bottomSigil: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 14,
    color: "#FFE09A",
    opacity: 0.78,
    marginTop: 12,
    filter: "drop-shadow(0 0 12px rgba(218,165,32,0.45))"
  },
  installOverlay: {
    position: "fixed",
    inset: 0,
    zIndex: 9999,
    background: "rgba(0,0,0,0.62)",
    backdropFilter: "blur(8px)",
    WebkitBackdropFilter: "blur(8px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "14px",
    paddingTop: "calc(14px + env(safe-area-inset-top, 0px))",
    paddingBottom: "calc(14px + env(safe-area-inset-bottom, 0px))"
  },
  installDialog: {
    width: "min(560px, 96vw)",
    borderRadius: 22,
    border: "1px solid rgba(232,95,255,0.48)",
    background: "linear-gradient(180deg, rgba(18,4,38,0.92), rgba(6,2,18,0.9))",
    boxShadow: "0 18px 48px rgba(0,0,0,0.66), 0 0 28px rgba(190,60,255,0.24)",
    color: "rgba(255,234,160,0.97)",
    padding: "16px 16px 14px",
    maxHeight: "calc(100vh - 120px)",
    overflow: "auto",
    WebkitOverflowScrolling: "touch",
    fontSize: "16px",
    lineHeight: 1.3
  },
  closeInstallBtn: {
    width: 42,
    height: 42,
    borderRadius: 14,
    border: "1px solid rgba(218,165,32,0.55)",
    background: "rgba(0,0,0,0.35)",
    color: "#FFEEA8",
    fontWeight: 900,
    cursor: "pointer",
    boxShadow: "0 10px 18px rgba(0,0,0,0.35)"
  },
  wheel: {
    display: "none"
  }
};
